// tourOpQueue.js - 2026-10-18
// Offline-kø for chaufførens handlinger på en tur: start (På vej), stop-kryds,
// billeder og afslutning. Samme mønster som receiptUploadQueue.js:
// localStorage overlever browser-luk og iOS-reload, og køen sendes igen ved
// app-start, når fanen bliver synlig, når nettet kommer tilbage og hvert 30. s.
//
// Kun netværksfejl (intet svar) og serverfejl (5xx) prøves igen. Siger
// serveren nej (4xx, fx "turen er taget af en anden"), fjernes handlingen og
// chaufføren får beskeden - den bliver ikke rigtig af at blive sendt igen.
//
// Afslutning sendes med et op_id, så serveren ignorerer en gentagelse
// (POST /tours/{id}/finish er sikker at sende to gange).
// 2026-10-24 (C7): Også stempler (IND/UD/pause). Tiden er den, chaufføren
// trykkede; serveren gemmer også, hvornår den kom frem (queued + op_id).
//
// 2026-10-25 (S1): Intet forsvinder uden at sige det.
// - 401 (login udløbet) sletter aldrig en handling: køen venter, til chaufføren
//   har logget ind igen (appen sender ham til login-siden).
// - Hver handling hører til den chauffør, der lavede den (uid). Logger en anden
//   ind på samme telefon, sendes de ikke med hans login.
// - Billeder ligger i IndexedDB (blobStore) - ikke i localStorage.
// - Fuld lagerplads: den NYE handling afvises med en besked; før blev de
//   ældste slettet (fx morgenens ind-stempling).
// Serveren skal tage imod det gamle format for alle kø-handlinger i mindst én
// release - en telefon kan have handlinger fra før en opdatering liggende.
//
// 2026-10-28 (H2): "For mange kald" (429) er ikke et nej - handlingen venter og sendes igen (før blev
// den slettet). Et stempel, serveren har et spørgsmål til (nattevagt? glemt UD?), parkeres med
// spørgsmålet (needs_answer) - appen spørger chaufføren, og svaret sender et nyt stempel og fjerner
// det parkerede. (K4): start og afslutning sender telefonens tryk-tidspunkt (started_at/finished_at).

import axios from "axios";
import { putBlob, getBlob, delBlob } from "./blobStore";

const KEY = "cf:tourOps";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
let busy = false;
let listeners = [];
let started = false;
let pausedFor = "";   // login (token), køen venter på - et nyt login fortsætter

const load = () => { try { return JSON.parse(localStorage.getItem(KEY) || "[]"); } catch (_) { return []; } };
const save = (q) => { localStorage.setItem(KEY, JSON.stringify(q)); };   // kaster ved fuld lagerplads
const authHeader = () => String(axios.defaults.headers.common?.Authorization || "");

/** Den indloggede brugers id (fra login-tokenet) - "" uden login. */
export function currentUid() {
  try {
    const part = authHeader().replace(/^Bearer\s+/, "").split(".")[1] || "";
    return JSON.parse(atob(part.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(part.length / 4) * 4, "="))).sub || "";
  } catch (_) { return ""; }
}
const mine = (o) => !o.uid || o.uid === currentUid();
const notify = (event) => {
  const n = pendingCount();
  listeners.forEach(fn => { try { fn(n, event); } catch (_) { /* ignore */ } });
};

export const isNetworkError = (e) => !e?.response || (e.response.status >= 500 && e.response.status !== 501);
export const pendingOps = () => load().filter(mine);
export const pendingCount = () => load().filter(o => !o.parked && mine(o)).length;
/** Venter køen på, at chaufføren logger ind igen? */
export const authPaused = () => !!pausedFor && pausedFor === authHeader();
export function subscribeOps(fn) { listeners.push(fn); return () => { listeners = listeners.filter(l => l !== fn); }; }

export function newOpId() {
  return (window.crypto?.randomUUID?.() || `op_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`);
}

const FULL_MSG = "Telefonen har ikke plads til flere ventende handlinger. Find net, så sendes de - prøv så igen.";

/** Læg en handling i køen. kind: "put" | "stop" | "photo" | "finish" | "take" | "start" | "punch".
 * Svarer true, når den er gemt; false (og en besked), når telefonen er fuld.
 * Alt andet end billeder gemmes med det samme (før første await). */
export async function enqueueOp(op) {
  const qid = newOpId();
  let item = { ...op, qid, queued_at: Date.now(), attempts: 0, uid: currentUid() };
  if (op.kind === "photo" && op.image) {
    const ref = `op:${qid}`;
    try { await putBlob(ref, op.image); } catch (_) { notify({ type: "full", message: FULL_MSG }); return false; }
    item = { ...item, image: undefined, image_ref: ref };
  }
  const q = load();
  // Samme afslutning (op_id) eller samme stop-kryds lægges ikke i køen to gange.
  const dup = q.find(x => (op.op_id && x.op_id === op.op_id) || (op.kind === "stop" && x.kind === "stop" && x.tour_id === op.tour_id && x.stop_id === op.stop_id));
  if (dup) Object.assign(dup, { ...op, uid: item.uid });
  else q.push(item);
  try { save(q); } catch (_) {
    if (item.image_ref) delBlob(item.image_ref);
    notify({ type: "full", message: FULL_MSG });
    return false;
  }
  notify();
  setTimeout(processOps, 1500);
  return true;
}

async function send(op) {
  if (op.kind === "put") return axios.put(`${API}/tours/${op.tour_id}`, op.body, { timeout: 30000 });
  if (op.kind === "stop") return axios.post(`${API}/tours/${op.tour_id}/stops/${op.stop_id}/done`, op.body, { timeout: 30000 });
  if (op.kind === "photo") {
    const image = op.image || (op.image_ref ? await getBlob(op.image_ref) : null);
    if (!image) { const e = new Error("Billedet findes ikke længere på telefonen"); e.response = { status: 410, data: { detail: e.message } }; throw e; }
    return axios.post(`${API}/tours/${op.tour_id}/photos`, { image }, { timeout: 60000 });
  }
  if (op.kind === "finish") return axios.post(`${API}/tours/${op.tour_id}/finish`, { ...op.body, op_id: op.op_id }, { timeout: 45000 });
  // 2026-10-20: "Tag turen" og start (evt. flere ture sammen på hængeren).
  if (op.kind === "take") return axios.post(`${API}/driver/tours/${op.tour_id}/take`, {}, { timeout: 30000 });
  // 2026-10-28: queued - appen spurgte om stemplet, da turen blev startet uden net (serveren spørger ikke igen).
  if (op.kind === "start") return axios.post(`${API}/driver/tours/start`, { tour_ids: op.tour_ids, op_id: op.op_id, queued: true, ...(op.started_at ? { started_at: op.started_at } : {}) }, { timeout: 30000 });
  if (op.kind === "punch") return axios.post(`${API}/driver/time-entries/punch`, { ...op.body, op_id: op.op_id, queued: true }, { timeout: 30000 });
  return null;
}

const drop = (op) => {
  try { save(load().filter(x => x.qid !== op.qid)); } catch (_) { /* fjern lykkes altid i praksis */ }
  if (op.image_ref) delBlob(op.image_ref);
};
/** Fjern en parkeret handling (fx et stempel, chaufføren har svaret på - svaret sendte et nyt). */
export function dropOp(qid) { const op = load().find(x => x.qid === qid); if (op) drop(op); notify(); }

// 2026-10-24 (G): En afslutning (vægt, vejeseddel, billeder) på en tur, kunden
// annullerede, mens chaufføren var uden net, blev slettet med beskeden. Nu
// gemmes den på telefonen og sendes igen hvert 10. minut i 7 dage - genoptager
// planlæggeren bestillingen ("kørt alligevel"), kommer registreringen frem.
const PARK_RETRY_MS = 10 * 60 * 1000;
const PARK_MAX_MS = 7 * 24 * 3600 * 1000;
const isCancelled = (e) => e?.response?.status === 409 && /annuller/i.test(String(e?.response?.data?.detail || ""));

export async function processOps() {
  if (busy || typeof navigator !== "undefined" && navigator.onLine === false) return;
  if (!authHeader()) return; // ikke logget ind endnu
  if (authPaused()) return;  // login udløbet - venter på et nyt login
  busy = true;
  try {
    // Rækkefølgen bevares: en tur startes før den afsluttes, billeder før afslutning.
    const now = Date.now();
    const ops = load().filter(mine);
    const due = [...ops.filter(o => !o.parked), ...ops.filter(o => o.parked && !o.needs_answer && now - (o.parked_try || 0) >= PARK_RETRY_MS)];
    for (const op of due) {
      // 2026-10-28 (H2): Efter et 429 venter handlingen til genforsøgstiden - rækkefølgen bevares.
      if (!op.parked && (op.retry_after || 0) > now) break;
      try {
        await send(op);
        drop(op);
        notify({ type: "sent", op });
      } catch (e) {
        if (e?.response?.status === 401) {
          // Login udløbet eller lukket: handlingen bliver - sendes efter nyt login.
          pausedFor = authHeader();
          notify({ type: "auth", message: "Log ind igen - det, der venter på telefonen, sendes bagefter." });
          break;
        }
        if (e?.response?.status === 429) {
          // 2026-10-28 (H2): Serveren bad om en pause (for mange kald) - ikke et nej. Vent 5 s, 10 s ... højst 60 s.
          const q = load();
          const it = q.find(x => x.qid === op.qid);
          if (it) { it.attempts += 1; it.retry_after = Date.now() + Math.min(60000, 5000 * it.attempts); try { save(q); } catch (_) { /* ignore */ } }
          break;
        }
        if (isNetworkError(e)) {
          const q = load();
          const it = q.find(x => x.qid === op.qid);
          if (it) { it.attempts += 1; try { save(q); } catch (_) { /* ignore */ } }
          if (op.parked) continue;
          break; // stadig offline - prøv igen senere, i samme rækkefølge
        }
        const d = e?.response?.data?.detail;
        if (op.kind === "punch" && e?.response?.status === 409 && d && typeof d === "object" && ["overnight_confirm", "yesterday_open", "old_open", "tours_on_way"].includes(d.code)) {
          // 2026-10-28 (H2): Et stempel, serveren har et spørgsmål til, smides ikke væk - det parkeres med
          // spørgsmålet, og appen spørger chaufføren. Før forsvandt stemplet, og timerne løb videre.
          const q = load();
          const it = q.find(x => x.qid === op.qid);
          if (it) {
            const first = !it.needs_answer;
            Object.assign(it, { parked: true, needs_answer: d, parked_at: it.parked_at || now, parked_try: now });
            try { save(q); } catch (_) { /* ignore */ }
            if (first) notify({ type: "needs_answer", op: it, detail: d });
          }
          continue;
        }
        if ((op.kind === "finish" || op.kind === "photo") && isCancelled(e) && now - (op.parked_at || now) < PARK_MAX_MS) {
          const q = load();
          const it = q.find(x => x.qid === op.qid);
          if (it) {
            const first = !it.parked;
            Object.assign(it, { parked: true, parked_at: it.parked_at || now, parked_try: now });
            try { save(q); } catch (_) { /* ignore */ }
            if (first) notify({ type: "parked", op, message: "Kunden har annulleret turen. Din registrering er gemt på telefonen og sendes, hvis planlæggeren genoptager bestillingen - ring til planlæggeren." });
          }
          continue;
        }
        drop(op);
        notify({ type: "rejected", op, message: (d && typeof d === "object" ? d.message : d) || "Serveren afviste handlingen" });
      }
    }
  } finally { busy = false; }
}

export function initTourOpQueue() {
  if (started) return;
  started = true;
  window.addEventListener("online", () => processOps());
  document.addEventListener("visibilitychange", () => { if (document.visibilityState === "visible") processOps(); });
  setInterval(processOps, 30000);
  setTimeout(processOps, 2000);
}
